import { defineStore, storeToRefs } from 'pinia'
import { ref, readonly, computed } from 'vue'
import {
  loadPromoCodeFromStorage,
  savePromoCodeToStorage,
} from '@/components/stripeCoupon/couponStorage'
import createLogger from '@/utils/logger'
import { useCustomerStore } from './customer'
import { useBrandingStore } from './branding'

const logger = createLogger('stores/stripeCoupons.ts')

export const useStripeCouponsStore = defineStore('stripeCoupons', () => {
  const promoCodeFromLocalStorage = ref<string | null>(null)
  const customerStore = useCustomerStore()
  const brandingStore = useBrandingStore()
  const { promoCode: promoCodeFromBrandingStore, isLoading: isLoadingBrandingStore } =
    storeToRefs(brandingStore)
  const { signupCompleted, isLoading: isLoadingCustomerStore } = storeToRefs(customerStore)
  const isLocalStorageLoading = ref(true)

  const isLoading = computed(
    () =>
      isLoadingBrandingStore.value || isLoadingCustomerStore.value || isLocalStorageLoading.value,
  )

  const savePromoCode = (
    newPromoCode: string | null,
    options: { overwrite: boolean } = { overwrite: true },
  ) => {
    logger.log('Saving coupon to storage', { newPromoCode })
    if (!options.overwrite && promoCodeFromLocalStorage.value) {
      logger.log('Promo code already exists, not overwriting')
      return
    }
    promoCodeFromLocalStorage.value = newPromoCode
    if (!signupCompleted.value) {
      savePromoCodeToStorage(newPromoCode)
    } else {
      if (newPromoCode) {
        logger.log('Signup completed, not saving coupon to storage')
      }
      savePromoCodeToStorage(null)
    }
  }

  const getPromoCode = () => {
    if (!promoCodeFromLocalStorage.value) {
      promoCodeFromLocalStorage.value = loadPromoCodeFromStorage()
    }
    isLocalStorageLoading.value = false
    return promoCodeFromLocalStorage.value
  }

  const removePromoCode = () => {
    promoCodeFromLocalStorage.value = null
    savePromoCodeToStorage(null)
  }

  const promoCode = computed(() => {
    return promoCodeFromLocalStorage.value || promoCodeFromBrandingStore.value
  })

  getPromoCode()

  return {
    savePromoCode,
    getPromoCode,
    removePromoCode,
    promoCode: readonly(promoCode),
    isLoading,
  }
})
